import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import GovernmentMarquee from '../components/GovernmentMarquee';
import HeroSlider from '../components/HeroSlider';
import './Home.css';
import usePageSearchIndex from '../hooks/usePageSearchIndex';

const Home = () => {
  const { t, isMarathi } = useLanguage();

  useEffect(() => {
    // Keep any existing side-effects (currently none besides search)
  }, []);

  // Auto-index visible Home page content for search
  usePageSearchIndex({
    mr: ['मुख्यपृष्ठ', 'योजना', 'उपक्रम', 'कार्यक्रम', 'पुरस्कार', 'डॅशबोर्ड'],
    en: ['home', 'schemes', 'activities', 'programs', 'awards', 'dashboard']
  });

  // Scroll-triggered animation for leadership cards
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
          observer.unobserve(entry.target); // Only animate once
        }
      });
    }, observerOptions);

    // Observe all leadership cards
    const leadershipCards = document.querySelectorAll('.leadership-card');
    leadershipCards.forEach((card) => {
      observer.observe(card);
    });

    return () => {
      leadershipCards.forEach((card) => {
        observer.unobserve(card);
      });
    };
  }, []);

  return (
    <div className="home">
      {/* Main Content */}
      <div className="home-content">
        <div className="container">
          {/* Hero Slider Section */}
          <section className="hero-slider-section">
            <HeroSlider />
          </section>

          {/* Welcome Note Section */}
          <section className="welcome-note-section" data-read-section="welcome-note">
            <div className="welcome-note-header">
              <h2 className="welcome-note-title">
                {isMarathi() 
                  ? 'महाराष्ट्र कामगार कल्याण मंडळाच्या अधिकृत संकेतस्थळावर आपले हार्दिक स्वागत.' 
                  : 'Welcome to the official website of Maharashtra Labours Welfare Board'}
              </h2>
            </div>
            <div className="welcome-note-content">  
              <p className="welcome-note-text">
                {isMarathi() ? (
                  <>
                    राज्यातील नोंदणीकृत कामगार व त्यांच्या कुटुंबियांच्या सर्वांगीण विकासासाठी आम्ही शिस्त, सेवा, सुधार आणि समृद्धी या ब्रीदवाक्यानुसार कार्यरत आहोत. शैक्षणिक, क्रीडा, सांस्कृतिक, सामाजिक, आरोग्य व रोजगारविषयक विविध कल्याणकारी योजना मंडळामार्फत राबविण्यात येतात. कामगारांच्या जीवनमानाचा स्तर उंचावणे आणि स्वावलंबनाला प्रोत्साहन देणे हेच आमचे ध्येय आहे. उत्कृष्ट सुविधा, आधुनिक केंद्रे व नियोजनबद्ध सेवा यांच्यासह कामगार कल्याणाच्या कार्यात महाराष्ट्र अग्रगण्य आहे. आपले सहकार्य आणि सहभाग आम्हाला सतत प्रगतीच्या दिशेने प्रेरित करीत राहतील.
                  </>
                ) : (
                  <>
                    We are working under the motto of Discipline, Service, Reform and Prosperity for the all-round development of the registered labours and their families in the state. Various welfare schemes in the field of education, sports, cultural, social, health and employment are implemented through the board. Our aim is to raise the standard of living of the labours and promote self-reliance. Maharashtra is at the forefront of the work of labours welfare with excellent facilities, modern centers and planned services. Your cooperation and participation will continue to motivate us towards continuous progress.
                  </>
                )}
              </p>
              <div className="welcome-note-button-container">
                <Link to="/overview" className="welcome-note-read-more-btn">
                  {isMarathi() ? 'अधिक वाचा' : 'Read More'}
                  <svg className="welcome-note-btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9,18 15,12 9,6"></polyline>
                  </svg>
                </Link>
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="features-section" data-read-section="boards-work">
            <div className="features-header">
              <h2 className="features-title">
                {t('boardsWork')}
              </h2>
            </div>
            <div className="features-grid">
              <Link to="/schemes" className="feature-card-link">
                <div className="feature-card">
                  <div className="feature-image">
                    <img 
                      src="/images/feature-card/feature-card-1-yojana.webp" 
                      alt="Schemes"
                      className="service-image"
                    />
                  </div>
                  <h3 className="feature-title">
                    {isMarathi() ? 'योजना' : 'Schemes'}
                  </h3>
                </div>
              </Link>
              
              <Link to="/activities" className="feature-card-link">
                <div className="feature-card">
                  <div className="feature-image">
                    <img 
                      src="/images/feature-card/feature-card-3-upakram.webp" 
                      alt="Activities"
                      className="service-image"
                    />
                  </div>
                  <h3 className="feature-title">
                    {isMarathi() ? 'उपक्रम' : 'Activities'}
                  </h3>
                </div>
              </Link>
              
              <Link to="/programs" className="feature-card-link">
                <div className="feature-card">
                  <div className="feature-image">
                    <img 
                      src="/images/feature-card/feature-card-2-karyakram.webp" 
                      alt="Programs"
                      className="service-image"
                    />
                  </div>
                  <h3 className="feature-title">
                    {isMarathi() ? 'कार्यक्रम' : 'Programs'}
                  </h3>
                </div>
              </Link>
              
              <Link to="/awards" className="feature-card-link">
                <div className="feature-card">
                  <div className="feature-image">
                    <img 
                      src="/images/feature-card/feature-card-4-puraskar.webp" 
                      alt="Awards"
                      className="service-image"
                    />
                  </div>
                  <h3 className="feature-title">
                    {isMarathi() ? 'पुरस्कार' : 'Awards'}
                  </h3>
                </div>
              </Link>
            </div>
            
          </section>

          {/* Leadership Section */}
          <section className="leadership-section" data-read-section="leadership-info">
            <div className="leadership-grid">
              {/* Leadership Card 1 */}
              <div className="leadership-card">
                <div className="leadership-photo">
                  <img 
                    src="/images/leadership-cards/leadership-photo-card-1.JPG" 
                    alt="Leadership Member 1"
                    className="photo-image"
                  />
                </div>
                <div className="leadership-info">
                  <h3 className="leadership-name">
                    {isMarathi() ? 'मा. ना. श्री. देवेंद्र फडणवीस' : 'Hon. Shri. Devendra Fadnavis'}
                  </h3>
                  <p className="leadership-designation">
                    {isMarathi() ? 'मुख्यमंत्री, महाराष्ट्र राज्य' : 'Hon. Chief Minister, Maharashtra State'}
                  </p>
                </div>
              </div>

              {/* Leadership Card 2 */}
              <div className="leadership-card">
                <div className="leadership-photo">
                  <img 
                    src="/images/leadership-cards/leadership-photo-card-2.JPG" 
                    alt="Leadership Member 2"
                    className="photo-image"
                  />
                </div>
                <div className="leadership-info">
                  <h3 className="leadership-name">
                    {isMarathi() ? 'मा. ना. श्री. एकनाथ शिंदे' : 'Hon. Shri. Eknath Shinde'}
                  </h3>
                  <p className="leadership-designation">
                    {isMarathi() ? 'उपमुख्यमंत्री, महाराष्ट्र राज्य' : 'Hon. Deputy Chief Minister, Maharashtra State'}
                  </p>
                </div>
              </div>

              {/* Leadership Card 3 */}
              <div className="leadership-card">
                <div className="leadership-photo">
                  <img 
                    src="/images/leadership-cards/leadership-photo-card-3.JPG" 
                    alt="Leadership Member 3"
                    className="photo-image"
                  />
                </div>
                <div className="leadership-info">
                  <h3 className="leadership-name">
                    {isMarathi() ? 'मा. श्रीमती सुनेत्रा अजित पवार' : 'Hon. Mrs Sunetra Ajit Pawar'}
                  </h3>
                  <p className="leadership-designation">
                    {isMarathi() ? 'उपमुख्यमंत्री, महाराष्ट्र राज्य' : 'Hon. Deputy Chief Minister, Maharashtra State'}
                  </p>
                </div>
              </div>

              {/* Leadership Card 4 */}
              <div className="leadership-card">
                <div className="leadership-photo">
                  <img 
                    src="/images/leadership-cards/leadership-photo-card-4.JPG" 
                    alt="Leadership Member 4"
                    className="photo-image"
                  />
                </div>
                <div className="leadership-info">
                  <h3 className="leadership-name">
                    {isMarathi() ? 'मा. ना. ॲड. आकाश फुंडकर' : 'Hon. Adv. Akash Fundkar'}
                  </h3>
                  <p className="leadership-designation">
                    {isMarathi() ? 'मंत्री, कामगार, महाराष्ट्र राज्य' : 'Minister, Labour, Maharashtra State'}
                  </p>
                </div>
              </div>

              {/* Leadership Card 5 */}
              <div className="leadership-card">
                <div className="leadership-photo">
                  <img 
                    src="/images/leadership-cards/leadership-photo-card-5.JPG" 
                    alt="Leadership Member 5"
                    className="photo-image"
                  />
                </div>
                <div className="leadership-info">
                  <h3 className="leadership-name">
                    {isMarathi() ? 'मा. ना. ॲड. आशिष जयस्वाल' : 'Hon. Adv. Ashish Jayswal'}
                  </h3>
                  <p className="leadership-designation">
                    {isMarathi() ? 'राज्यमंत्री, वित्त, नियोजन, कृषी, मदत व पुनर्वसन, विधी व न्याय आणि कामगार, महाराष्ट्र राज्य' : 'State Minister, Finance, Planning, Agriculture, Relief & Rehabilitation, Law & Justice and Labour, Maharashtra State'}
                  </p>
                </div>
              </div>

            </div>
            
          </section>

          {/* Dashboard Section - Hidden */}
          {/* <section className="dashboard-section">
            <Dashboard />
          </section> */}

          {/* Circulars Section */}
          <section className="circulars-section">
            <div className="circulars-grid">
              <div className="circulars-card" data-read-section="quick-links">
                <h3 className="circulars-title">
                  {t('quickLinks')}
                </h3>
                <div className="quick-links-list">
                  <a 
                    href="https://mlwb.in/react-static-files/pdf/homepage/Quick-Links/MLWB-Fund-Act-and-Rules-marathi-version.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="quick-link-item"
                  >
                    <svg className="quick-link-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    <span className="quick-link-text">{t('actMarathi')}</span>
                  </a>
                  
                  <a 
                    href="https://mlwb.in/react-static-files/pdf/homepage/Quick-Links/MLWB-Fund-Act-and-Rules-english-version.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="quick-link-item"
                  >
                    <svg className="quick-link-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    <span className="quick-link-text">{t('actEnglish')}</span>
                  </a>
                  
                  <a 
                    href="https://mlwb.in/react-static-files/pdf/homepage/Quick-Links/Notification-6BB.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="quick-link-item"
                  >
                    <svg className="quick-link-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    <span className="quick-link-text">{t('notification')}</span>
                  </a>
                  
                  <a 
                    href="https://mlwb.in/react-static-files/pdf/homepage/Quick-Links/Citizens-Charter.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="quick-link-item"
                  >
                    <svg className="quick-link-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    <span className="quick-link-text">{t('citizensCharter')}</span>
                  </a>
                  
                  <Link to="/annual-report" className="quick-link-item">
                    <svg className="quick-link-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    <span className="quick-link-text">{t('annualReport')}</span>
                  </Link>
                </div>
              </div>
              
              <div className="circulars-card" data-read-section="important-circulars">
                <h3 className="circulars-title">
                  {t('importantCirculars')}
                </h3>
                <div className="quick-links-list">
                  <a 
                    href="https://mlwb.in/react-static-files/pdf/homepage/Important-Circulars/maidan-arakshan.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="quick-link-item"
                  >
                    <svg className="quick-link-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    <span className="quick-link-text">{t('groundReservation')}</span>
                  </a>
                  
                  <a 
                    href="https://mlwb.in/react-static-files/pdf/homepage/Important-Circulars/shulk-2014-pad-466.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="quick-link-item"
                  >
                    <svg className="quick-link-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    <span className="quick-link-text">{t('variousFees')}</span>
                  </a>
                  
                  <a 
                    href="https://mlwb.in/react-static-files/pdf/homepage/Important-Circulars/kalbaddha-karyakram.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="quick-link-item"
                  >
                    <svg className="quick-link-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    <span className="quick-link-text">{t('timeboundProgram')}</span>
                  </a>
                  
                  <a 
                    href="https://mlwb.in/react-static-files/pdf/homepage/Important-Circulars/Rashtriya-din-sajara-karnebabat.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="quick-link-item"
                  >
                    <svg className="quick-link-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    <span className="quick-link-text">{t('nationalDayCelebration')}</span>
                  </a> 
                  
                  <div className="more-button-container">
                    <Link to="/important-circulars" className="more-button">
                      {isMarathi() ? '« आणखी »' : '« More »'}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Government Links Marquee */}
          <GovernmentMarquee />
        </div>
      </div>
    </div>
  );
};

export default Home;
